/**
 * Portal Theme for Mantine
 *
 * Тема Mantine для It Is Interesting Portal.
 * Использует design tokens из designTokens.js
 *
 * Применение:
 * <MantineProvider theme={portalTheme}>...</MantineProvider>
 */

import { createTheme } from '@mantine/core';
import tokens from './designTokens';

const { colors, typography, spacing, borders, shadows, transitions } = tokens;

// ============================================================================
// ГЕНЕРАЦИЯ ЦВЕТОВЫХ ШКАЛ ДЛЯ MANTINE
// ============================================================================

/**
 * Mantine требует массив из 10 оттенков для каждого цвета
 * Генерируем шкалы на основе корпоративных цветов
 */

// Portal Blue Scale
const portalBlueScale = [
  '#E5F1FA',  // 0 - lightest (info background)
  '#C2E0F7',  // 1
  '#9BCEF4',  // 2
  '#6FB9F0',  // 3
  '#4AA3ED',  // 4
  colors.brand.primary,  // 5 - main (#0176D3)
  colors.brand.hover,    // 6 - hover (#015BA5)
  colors.brand.dark,     // 7 - dark (#014486)
  '#003A6D',  // 8
  colors.brand.active,   // 9 - darkest (#01344A)
];

// Success Green Scale
const successGreenScale = [
  colors.semantic.success.background,  // 0 - lightest (#EBF9F1)
  '#D7F3E1',  // 1
  '#A8E6C0',  // 2
  '#78D89F',  // 3
  colors.semantic.success.light,       // 4 - light (#4BCA81)
  colors.semantic.success.primary,     // 5 - main (#2E844A)
  '#26733F',  // 6
  colors.semantic.success.dark,        // 7 - dark (#194D2F)
  '#0F3B20',  // 8
  '#062913',  // 9 - darkest
];

// Error Red Scale
const errorRedScale = [
  colors.semantic.error.background,  // 0 - lightest (#FEDED8)
  '#FBBFB8',  // 1
  '#F89F99',  // 2
  '#F67F79',  // 3
  colors.semantic.error.light,       // 4 - light (#EA001E)
  colors.semantic.error.primary,     // 5 - main (#C23934)
  '#A72F2A',  // 6
  colors.semantic.error.dark,        // 7 - dark (#8C1A15)
  '#6D140F',  // 8
  '#4D0E0A',  // 9 - darkest
];

// Warning Orange Scale
const warningOrangeScale = [
  colors.semantic.warning.background,  // 0 - lightest (#FFF5E8)
  '#FFE8C2',  // 1
  '#FFDA9C',  // 2
  '#FFCC76',  // 3
  colors.semantic.warning.primary,     // 4 - main (#FFA429)
  '#E69320',  // 5
  colors.semantic.warning.dark,        // 6 - dark (#DD7A01)
  '#B66201',  // 7
  '#8F4B01',  // 8
  '#683501',  // 9 - darkest
];

// Gray Scale (нейтральные с синим подтоном)
const grayScale = [
  colors.neutral[1],   // 0 - #FAFAF9
  colors.neutral[2],   // 1 - #F3F3F3
  colors.neutral[3],   // 2 - #ECEBEA
  colors.neutral[4],   // 3 - #DDDBDA
  colors.neutral[5],   // 4 - #C9C7C5
  colors.neutral[6],   // 5 - #B0ADAB
  colors.neutral[7],   // 6 - #706E6B
  colors.neutral[8],   // 7 - #514F4D
  colors.neutral[9],   // 8 - #3E3E3C
  colors.neutral[10],  // 9 - #181818
];

// ============================================================================
// СОЗДАНИЕ ТЕМЫ MANTINE
// ============================================================================

export const portalTheme = createTheme({
  // ========== PRIMARY COLOR ==========
  primaryColor: 'portal-blue',
  primaryShade: 5, // Основной оттенок (#0176D3)

  // ========== CUSTOM COLORS ==========
  colors: {
    'portal-blue': portalBlueScale,
    'portal-green': successGreenScale,
    'portal-red': errorRedScale,
    'portal-orange': warningOrangeScale,
    'portal-gray': grayScale,
  },

  // ========== TYPOGRAPHY ==========
  fontFamily: typography.fontFamily.primary,
  fontFamilyMonospace: typography.fontFamily.monospace,

  // Font sizes - маппим на Mantine размеры
  fontSizes: {
    xs: typography.fontSize[12],   // 12px
    sm: typography.fontSize[13],   // 13px
    md: typography.fontSize[14],   // 14px - базовый
    lg: typography.fontSize[16],   // 16px
    xl: typography.fontSize[18],   // 18px
  },

  // Headings configuration
  headings: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.bold,
    sizes: {
      h1: {
        fontSize: typography.fontSize[40],
        lineHeight: typography.lineHeight.tight,
        fontWeight: typography.fontWeight.bold,
        color: colors.neutral[10],
      },
      h2: {
        fontSize: typography.fontSize[32],
        lineHeight: typography.lineHeight.tight,
        fontWeight: typography.fontWeight.bold,
        color: colors.neutral[9],
      },
      h3: {
        fontSize: typography.fontSize[24],
        lineHeight: typography.lineHeight.tight,
        fontWeight: typography.fontWeight.semibold,
        color: colors.neutral[9],
      },
      h4: {
        fontSize: typography.fontSize[20],
        lineHeight: typography.lineHeight.normal,
        fontWeight: typography.fontWeight.semibold,
        color: colors.neutral[8],
      },
      h5: {
        fontSize: typography.fontSize[18],
        lineHeight: typography.lineHeight.normal,
        fontWeight: typography.fontWeight.semibold,
        color: colors.neutral[8],
      },
      h6: {
        fontSize: typography.fontSize[16],
        lineHeight: typography.lineHeight.normal,
        fontWeight: typography.fontWeight.semibold,
        color: colors.neutral[8],
      },
    },
  },

  // ========== SPACING ==========
  spacing: {
    xs: spacing.xs,    // 8px
    sm: spacing.sm,    // 12px
    md: spacing.lg,    // 24px - увеличенный базовый для лучшей читаемости
    lg: spacing.xl,    // 32px
    xl: spacing['2xl'], // 48px
  },

  // ========== RADIUS ==========
  radius: {
    xs: borders.radius.sm,   // 2px
    sm: borders.radius.md,   // 4px - стандарт
    md: borders.radius.lg,   // 8px
    lg: borders.radius.xl,   // 12px
    xl: '16px',
  },
  defaultRadius: 'sm', // 4px - скругленные углы

  // ========== SHADOWS ==========
  shadows: {
    xs: shadows[1],  // Едва заметная
    sm: shadows[2],  // Карточки
    md: shadows[3],  // Поднятые элементы
    lg: shadows[4],  // Модальные окна
    xl: shadows[5],  // Выпадающие меню
  },

  // ========== OTHER ==========
  lineHeights: {
    xs: typography.lineHeight.tight,
    sm: typography.lineHeight.tight,
    md: typography.lineHeight.normal,
    lg: typography.lineHeight.normal,
    xl: typography.lineHeight.relaxed,
  },

  // ========== COMPONENT DEFAULTS ==========
  components: {
    // ---------- Button ----------
    Button: {
      defaultProps: {
        radius: 'xl', // Современный скруглённый стиль
      },
      styles: {
        root: {
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize[14],
          transition: transitions.all,

          '&[data-variant="light"]': {
            color: colors.brand.primary,
            backgroundColor: colors.semantic.info.background,
            '&:hover': {
              backgroundColor: colors.neutral[2],
            },
          },

          '&[data-variant="outline"]': {
            borderColor: colors.brand.primary,
            color: colors.brand.primary,
            '&:hover': {
              backgroundColor: colors.semantic.info.background,
            },
          },
        },
      },
    },

    // ---------- Input ----------
    Input: {
      defaultProps: {
        radius: 'xl', // Современный скруглённый стиль
        size: 'md',
      },
      styles: {
        input: {
          height: '40px',
          borderColor: borders.color.default,
          fontSize: typography.fontSize[14],
          transition: transitions.border,

          '&:hover': {
            borderColor: borders.color.hover,
          },

          '&:focus': {
            borderColor: borders.color.focus,
            borderWidth: borders.width.thick,
            boxShadow: shadows.focus,
          },

          '&[data-invalid]': {
            borderColor: borders.color.error,
            borderWidth: borders.width.thick,
            '&:focus': {
              boxShadow: shadows.errorFocus,
            },
          },
        },
        wrapper: {
          marginBottom: spacing[2], // 8px
        },
        label: {
          fontSize: typography.fontSize[13],
          fontWeight: typography.fontWeight.medium,
          color: colors.neutral[8],
          marginBottom: spacing[1], // 4px
        },
        description: {
          fontSize: typography.fontSize[12],
          color: colors.neutral[7],
          marginTop: spacing[1],
        },
        error: {
          fontSize: typography.fontSize[12],
          color: colors.semantic.error.primary,
          marginTop: spacing[1],
        },
      },
    },

    // ---------- TextInput ----------
    TextInput: {
      defaultProps: {
        radius: 'xl', // Современный скруглённый стиль
        size: 'md',
      },
    },

    // ---------- Select ----------
    Select: {
      defaultProps: {
        radius: 'xl', // Современный скруглённый стиль
        size: 'md',
      },
    },

    // ---------- PasswordInput ----------
    PasswordInput: {
      defaultProps: {
        radius: 'xl', // Современный скруглённый стиль
        size: 'md',
      },
    },

    // ---------- Card ----------
    Card: {
      defaultProps: {
        radius: 'lg', // 12px - более скругленные края
        shadow: 'md',
        padding: 'xl', // 32px
      },
      styles: {
        root: {
          backgroundColor: colors.neutral.white,
          border: `${borders.width.thin} solid ${borders.color.default}`,
          transition: transitions.all,
          '&:hover': {
            boxShadow: shadows[4],
            transform: 'translateY(-2px)',
          },
        },
      },
    },

    // ---------- Modal ----------
    Modal: {
      defaultProps: {
        radius: 'xl',
        shadow: 'lg',
        padding: 0,
      },
      styles: {
        content: {
          borderRadius: '16px',
        },
        header: {
          padding: '24px 24px 12px',
          borderBottom: `${borders.width.thin} solid ${colors.neutral[3]}`,
        },
        body: {
          padding: '24px',
        },
        title: {
          fontSize: typography.fontSize[20],
          fontWeight: typography.fontWeight.semibold,
          color: colors.neutral[9],
        },
      },
    },

    // ---------- Table ----------
    Table: {
      styles: {
        table: {
          borderCollapse: 'collapse',
        },
        thead: {
          backgroundColor: 'transparent',
        },
        th: {
          padding: '12px 16px',
          borderBottom: `${borders.width.thin} solid ${colors.neutral[3]}`,
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize[14],
          color: colors.neutral[8],
          textAlign: 'left',
        },
        td: {
          padding: '12px 16px',
          borderBottom: `${borders.width.thin} solid ${colors.neutral[3]}`,
          fontSize: typography.fontSize[14],
          color: colors.neutral[8],
        },
        tr: {
          transition: transitions.background,
          '&:hover td': {
            backgroundColor: colors.neutral[2],
          },
        },
      },
    },

    // ---------- Badge ----------
    Badge: {
      defaultProps: {
        radius: 'sm',
      },
      styles: {
        root: {
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize[12],
          textTransform: 'none',
        },
      },
    },

    // ---------- ActionIcon ----------
    ActionIcon: {
      defaultProps: {
        radius: 'sm',
      },
      styles: {
        root: {
          transition: transitions.all,
        },
      },
    },

    // ---------- Menu ----------
    Menu: {
      defaultProps: {
        radius: 'lg',
      },
      styles: {
        dropdown: {
          borderRadius: '12px',
          border: `${borders.width.thin} solid ${colors.neutral[3]}`,
          boxShadow: shadows[3],
        },
        item: {
          borderRadius: '8px',
        },
      },
    },

    // ---------- Popover ----------
    Popover: {
      styles: {
        dropdown: {
          borderRadius: '12px',
          border: `${borders.width.thin} solid ${colors.neutral[3]}`,
          boxShadow: shadows[3],
        },
      },
    },

    // ---------- TimePicker ----------
    TimePicker: {
      defaultProps: {
        radius: 'xl',
        size: 'md',
      },
    },

    // ---------- Tooltip ----------
    Tooltip: {
      defaultProps: {
        radius: 'sm',
      },
      styles: {
        tooltip: {
          fontSize: typography.fontSize[12],
          backgroundColor: colors.neutral[9],
          color: colors.neutral.white,
        },
      },
    },

    // ---------- AppShell ----------
    AppShell: {
      styles: {
        main: {
          backgroundColor: colors.neutral[1], // #FAFAF9 - почти белый
          minHeight: '100vh',
        },
        header: {
          backgroundColor: colors.neutral.white,
          borderBottom: `${borders.width.thin} solid ${colors.neutral[3]}`,
          boxShadow: shadows[2],
        },
        navbar: {
          backgroundColor: colors.neutral.white,
          borderRight: `${borders.width.thin} solid ${colors.neutral[3]}`,
        },
      },
    },

    // ---------- Notification ----------
    Notification: {
      defaultProps: {
        radius: 'md',
      },
      styles: {
        root: {
          boxShadow: shadows[4],
        },
        title: {
          fontWeight: typography.fontWeight.semibold,
        },
      },
    },

    // ---------- Progress ----------
    Progress: {
      styles: {
        root: {
          backgroundColor: colors.neutral[3],
        },
        section: {
          backgroundColor: colors.brand.primary,
        },
      },
    },

    // ---------- Tabs ----------
    Tabs: {
      styles: {
        tab: {
          padding: '12px 16px',
          borderBottom: `3px solid transparent`,
          fontWeight: typography.fontWeight.regular,
          color: colors.neutral[7],
          transition: transitions.all,

          '&:hover': {
            borderBottomColor: colors.neutral[5],
            backgroundColor: colors.neutral[1],
          },

          '&[data-active]': {
            borderBottomColor: colors.brand.primary,
            color: colors.brand.primary,
            fontWeight: typography.fontWeight.semibold,
          },
        },
        tabLabel: {
          fontSize: typography.fontSize[14],
        },
      },
    },
  },

  // ========== CUSTOM THEME VARIABLES ==========
  other: {
    // Layout sizes
    headerHeight: '70px',
    sidebarWidth: '280px',

    // Custom colors для прямого доступа
    portalColors: colors,

    // Transitions
    transitions,

    // Borders
    borders,
  },
});

export default portalTheme;
